import { CAMPAIGN_TYPE } from '@/constants/accelerationTable'
import { setEndOfDay, setStartOfDay } from '@/utils/format/setHours'
import { objectDiff } from '@/utils/objectDiff/objectDiff'
import { businessUnit } from '@/utils/themes/theme'

import type { AccelerationFormFields, AccelerationFormState, UpdateCampaignPayload } from '../AccelerationForm.types'
import {
  normalizeCampaignBrandFlags,
  resolveSharedContentButtonUrl,
  serializeContentByBrandForApi
} from './brandForApi'
import { serializeAccelerationContentForApi } from './contentForApi'
import { serializeAccelerationPointsForApi } from './pointsForApi'

export const formatCampaignPayloadForApi = (formData: AccelerationFormFields | AccelerationFormState) => {
  const brand = normalizeCampaignBrandFlags(formData.brand)
  const contentByBrand = serializeContentByBrandForApi(brand, formData.contentByBrand ?? {})

  return {
    name: formData.name,
    type: formData.type,
    segmentation: {
      campaignType: formData.segmentation ? formData.segmentation.campaignType : '',
      audienceType: formData.segmentation ? formData.segmentation?.audienceType : ''
    },
    businessUnit: businessUnit(globalThis.location.hostname),
    responsible: '',
    startDate: setStartOfDay(formData.startDate ?? ''),
    endDate: setEndOfDay(formData.endDate ?? ''),
    points: formData.points ? serializeAccelerationPointsForApi(formData.points) : null,
    brand,
    contentByBrand,
    content: formData.content
      ? {
          ...serializeAccelerationContentForApi(formData.content),
          buttonUrl: resolveSharedContentButtonUrl(brand, formData.contentByBrand ?? {}, formData.content.buttonUrl)
        }
      : null,
    ...(formData.type === CAMPAIGN_TYPE.MULTIPLIER
      ? { multiplier: Number(formData.multiplier) }
      : { point: Number(formData.point) })
  }
}

export const getCampaignDiff = (
  initialValues: AccelerationFormFields,
  formData: AccelerationFormState
): UpdateCampaignPayload => {
  const campaign = formatCampaignPayloadForApi(formData)
  const formattedInitialValues = formatCampaignPayloadForApi(initialValues)
  const diff = objectDiff(formattedInitialValues, campaign) as UpdateCampaignPayload

  if (diff.brand) {
    diff.brand = campaign.brand
    diff.contentByBrand = campaign.contentByBrand
  } else if (diff.contentByBrand) {
    diff.contentByBrand = campaign.contentByBrand
  }

  return diff
}

export const hasCampaignChanges = (initialValues: AccelerationFormFields, formData: AccelerationFormState) => {
  const campaign = formatCampaignPayloadForApi(formData)
  const formattedInitialValues = formatCampaignPayloadForApi(initialValues)

  const diff = objectDiff(formattedInitialValues, campaign) as UpdateCampaignPayload

  return Object.keys(diff).length > 0
}
